import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Box,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { setCurrentView, toggleSidebar, setLoading, resetToLogin } from '../../store/appSlice';
import { signOut } from 'aws-amplify/auth';
import { persistor } from '../../store/store';
import { reset as resetAmplitude, track as trackAmplitude } from '../../services/amplitude';
import { NavigationRoute } from '../../types/enums';
import AccountSwitcherModal from '../common/AccountSwitcherModal';

// Import Material-UI icons
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import carbonSpaceLogo from '../../assets/logo.png';

const DRAWER_WIDTH = 220;
const COLLAPSED_DRAWER_WIDTH = 60;

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  '& .MuiDrawer-paper': {
    width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRight: 'none',
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  justifyContent: isCollapsed ? 'center' : 'flex-start',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
  },
}));

const BrandSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: isCollapsed ? 'center' : 'space-between',
  padding: theme.spacing(2, isCollapsed ? 2 : 2.5),
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const LogoImage = styled('img')({
  height: 40,
  width: 'auto',
});

const BottomSection = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  padding: theme.spacing(1),
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
}));

const navigationItems = [
  { route: NavigationRoute.DASHBOARD, label: 'Dashboard', icon: DashboardOutlinedIcon },
  { route: NavigationRoute.ANALYTICS, label: 'Analytics', icon: InsightsOutlinedIcon },
  { route: NavigationRoute.MAP, label: 'Map', icon: LocationOnOutlinedIcon },
];

const NavigationSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const currentView = useSelector((state: RootState) => state.app.currentView);
  const isSidebarCollapsed = useSelector((state: RootState) => state.app.isSidebarCollapsed);
  const { isAdmin, availableAccounts, selectedAccount } = useSelector((state: RootState) => state.app);
  const [isAccountModalOpen, setIsAccountModalOpen] = React.useState(false);

  const handleNavigation = (route: NavigationRoute) => {
    dispatch(setCurrentView(route));
    // Track page view in Amplitude
    trackAmplitude('Page Viewed', {
      page_name: route,
      page_title: route.charAt(0).toUpperCase() + route.slice(1).replace('_', ' '),
    });
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleAccountSwitcherClick = () => {
    setIsAccountModalOpen(true);
  };

  const handleAccountModalClose = () => {
    setIsAccountModalOpen(false);
  };

  const handleLogout = async () => {
    dispatch(setLoading(true));
    try {
      await signOut();
      // Reset Amplitude user identity
      resetAmplitude();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear Redux persist storage
      await persistor.purge();

      // Clear Redux state
      dispatch(resetToLogin());
      dispatch(setLoading(false));
    }
  };

  return (
    <StyledDrawer variant="permanent" anchor="left" isCollapsed={isSidebarCollapsed}>
      <Stack sx={{ height: '100%' }}>
        <BrandSection isCollapsed={isSidebarCollapsed}>
          {!isSidebarCollapsed && (
            <LogoImage src={carbonSpaceLogo} alt="CarbonSpace Logo" />
          )}
          <IconButton onClick={handleToggleSidebar} sx={{ color: 'inherit' }}>
            {isSidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </BrandSection>

        <List sx={{ flex: 1, px: 1 }}>
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <ListItem key={item.route} disablePadding>
                <StyledListItemButton
                  selected={currentView === item.route}
                  onClick={() => handleNavigation(item.route)}
                  isCollapsed={isSidebarCollapsed}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: isSidebarCollapsed ? 0 : 40 }}>
                    <IconComponent />
                  </ListItemIcon>
                  {!isSidebarCollapsed && <ListItemText primary={item.label} />}
                </StyledListItemButton>
              </ListItem>
            );
          })}

          {isAdmin && (
            <ListItem disablePadding>
              <StyledListItemButton
                selected={currentView === NavigationRoute.USER_MANAGEMENT}
                onClick={() => handleNavigation(NavigationRoute.USER_MANAGEMENT)}
                isCollapsed={isSidebarCollapsed}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: isSidebarCollapsed ? 0 : 40 }}>
                  <PeopleOutlineIcon />
                </ListItemIcon>
                {!isSidebarCollapsed && <ListItemText primary="User Management" />}
              </StyledListItemButton>
            </ListItem>
          )}

          {/* Account Switcher for Admins */}
          {isAdmin && availableAccounts.length > 0 && (
            <ListItem disablePadding>
              <StyledListItemButton isCollapsed={isSidebarCollapsed} onClick={handleAccountSwitcherClick}>
                <ListItemIcon sx={{ color: 'inherit', minWidth: isSidebarCollapsed ? 0 : 40 }}>
                  <SwapHorizIcon />
                </ListItemIcon>
                {!isSidebarCollapsed && (
                  <ListItemText
                    primary="Switch Account"
                    secondary={selectedAccount ? selectedAccount.name : undefined}
                  />
                )}
              </StyledListItemButton>
            </ListItem>
          )}
        </List>

        <BottomSection>
          <List>
            <ListItem disablePadding>
              <StyledListItemButton
                selected={currentView === NavigationRoute.KNOWLEDGE_BASE}
                onClick={() => handleNavigation(NavigationRoute.KNOWLEDGE_BASE)}
                isCollapsed={isSidebarCollapsed}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: isSidebarCollapsed ? 0 : 40 }}>
                  <MenuBookIcon />
                </ListItemIcon>
                {!isSidebarCollapsed && <ListItemText primary="Knowledge Base" />}
              </StyledListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <StyledListItemButton isCollapsed={isSidebarCollapsed} onClick={handleLogout}>
                <ListItemIcon sx={{ color: 'inherit', minWidth: isSidebarCollapsed ? 0 : 40 }}>
                  <MeetingRoomOutlinedIcon />
                </ListItemIcon>
                {!isSidebarCollapsed && <ListItemText primary="Logout" />}
              </StyledListItemButton>
            </ListItem>
          </List>
        </BottomSection>
      </Stack>

      {/* Account Switcher Modal */}
      <AccountSwitcherModal
        open={isAccountModalOpen}
        onClose={handleAccountModalClose}
      />
    </StyledDrawer>
  );
};

export default NavigationSidebar;
